import { FC } from 'react';
import { Box, Card, CardContent, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { BadgeOutlined, ChatBubbleOutline, Comment, DeleteOutline, Edit } from '@mui/icons-material';
import {
  formatSalesCallType,
  SalesCallAssigneeAvatar,
  SalesCallContactCell,
  SalesCallDateCell,
  SalesCallStatusChip,
} from './salesCallListDisplay';

interface SalesCallCardProps {
  data: any;
  onDailySalesReport: (id: number) => void;
  onFollowUp: (id: number) => void;
  onComment: (id: number) => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  showAssignee?: boolean;
}

const SalesCallCard: FC<SalesCallCardProps> = ({
  data,
  onDailySalesReport,
  onFollowUp,
  onComment,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  showAssignee = false,
}) => {
  const { id, title, followUpMode } = data;
  const displayTitle = title || '—';

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 1.5,
        border: theme => `1px solid ${theme.palette.divider}`,
        backgroundColor: '#FAFAFA',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <SalesCallContactCell row={data} />
          <Stack direction="row" spacing={1} alignItems="center">
            {showAssignee ? <SalesCallAssigneeAvatar row={data} /> : null}
            <SalesCallStatusChip row={data} />
          </Stack>
        </Stack>

        <Tooltip title={displayTitle === '—' ? '' : displayTitle}>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#101828',
              lineHeight: 1.4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}>
            {displayTitle}
          </Typography>
        </Tooltip>

        <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>
              Type
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: '#101828' }}>
              {formatSalesCallType(followUpMode)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>
              Date
            </Typography>
            <SalesCallDateCell row={data} />
          </Box>
        </Stack>

        <Divider sx={{ mt: 'auto' }} />

        <Stack
          direction="row"
          spacing={0.5}
          justifyContent="flex-end"
          flexWrap="wrap"
          alignItems="center"
          sx={{ gap: 0.5 }}>
          <Tooltip title="Daily Sales Report">
            <IconButton
              onClick={() => onDailySalesReport(id)}
              size="small"
              sx={{
                border: theme => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}>
              <BadgeOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Follow-up">
            <IconButton
              onClick={() => onFollowUp(id)}
              size="small"
              sx={{
                border: theme => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}>
              <ChatBubbleOutline fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Comment">
            <IconButton
              onClick={() => onComment(id)}
              size="small"
              sx={{
                border: theme => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}>
              <Comment fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEdit && (
            <Tooltip title="Edit">
              <IconButton
                onClick={() => onEdit?.(id)}
                size="small"
                sx={{
                  border: theme => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete">
              <IconButton
                onClick={() => onDelete?.(id)}
                size="small"
                sx={{
                  border: theme => `1px solid ${theme.palette.error.main}`,
                  borderRadius: 1,
                  color: 'error.main',
                  '&:hover': {
                    borderColor: 'error.dark',
                    backgroundColor: 'error.light',
                    color: 'error.dark',
                  },
                }}>
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SalesCallCard;
